import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { EmptyState } from "./EmptyState";
import { formatINR } from "../lib/format";

const PALETTE = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

const ChartTooltip = ({ active, payload, formatter }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
      <p className="font-medium">{item.name}</p>
      <p className="font-num mt-0.5 text-muted-foreground">{formatter(item.value)}</p>
    </div>
  );
};

export const PieCard = ({
  title,
  subtitle,
  data = [],
  testId,
  formatter = formatINR,
  emptyTitle = "Nothing to chart yet",
  emptyDescription = "Data will appear here once entries are added.",
  height = 240,
}) => {
  const clean = (data || []).filter((d) => Number(d.value) > 0);
  const total = clean.reduce((s, d) => s + Number(d.value || 0), 0);

  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </CardHeader>
      <CardContent>
        {clean.length === 0 ? (
          <EmptyState
            icon={PieIcon}
            title={emptyTitle}
            description={emptyDescription}
            className="border-0 bg-transparent py-8"
            testId={`${testId}-empty`}
          />
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div style={{ width: "100%", maxWidth: 220, height }} data-testid={testId}>
              <ResponsiveContainer width="100%" height="100%" minWidth={160} minHeight={180}>
                <PieChart>
                  <Pie
                    data={clean}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="58%"
                    outerRadius="85%"
                    paddingAngle={2}
                    stroke="hsl(var(--card))"
                    strokeWidth={2}
                    isAnimationActive={false}
                  >
                    {clean.map((entry, i) => (
                      <Cell key={entry.name} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={formatter} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="flex-1 space-y-2">
              {clean.map((entry, i) => (
                <li key={entry.name} className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
                    />
                    <span className="truncate text-muted-foreground">{entry.name}</span>
                  </span>
                  <span className="font-num shrink-0 text-right">
                    {formatter(entry.value)}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {total ? `${((entry.value / total) * 100).toFixed(1)}%` : ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
